import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { List, Route, AlertTriangle, RefreshCw, MapPinned, LocateFixed, Loader2 } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { DeadlineActionDialog } from "@/components/calendar/DeadlineActionDialog";
import { MachineMap } from "@/components/map/MachineMap";
import { MapToolbar } from "@/components/map/MapToolbar";
import { MapList } from "@/components/map/MapList";
import { MachineSheet } from "@/components/map/MachineSheet";
import { LocationSheet } from "@/components/map/LocationSheet";
import { TripPanel } from "@/components/map/TripPanel";
import { useDebounce } from "@/hooks/useDebounce";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { fetchMapData, routePlan, getCurrentPosition } from "@/lib/geo";
import { cn } from "@/lib/utils";

const parseStatus = (s) => (s ? s.split(",").filter((x) => ["overdue", "upcoming", "ok", "planned"].includes(x)) : []);

/**
 * Kart – maskiner og fristar på kart + turplanlegging.
 * URL-state: d (dagar), status, kind, customer, q, trip.
 */
export default function Kart() {
  const [params, setParams] = useSearchParams();
  const desktop = useMediaQuery("(min-width: 1024px)");
  const mapRef = useRef(null);

  // ---- filter (URL) ----
  const filters = useMemo(
    () => ({
      days: params.has("d") ? Number(params.get("d")) : 90,
      status: parseStatus(params.get("status")),
      kind: params.get("kind") || "",
      customer: params.get("customer") || "",
      q: params.get("q") || "",
    }),
    [params]
  );
  const tripMode = params.get("trip") === "1";
  const setFilters = useCallback(
    (patch) => {
      const p = new URLSearchParams(params);
      const next = { ...filters, ...patch };
      if (next.days === 90) p.delete("d"); else p.set("d", String(next.days));
      if (next.status.length) p.set("status", next.status.join(",")); else p.delete("status");
      if (next.kind) p.set("kind", next.kind); else p.delete("kind");
      if (next.customer) p.set("customer", next.customer); else p.delete("customer");
      if (next.q) p.set("q", next.q); else p.delete("q");
      setParams(p, { replace: true });
    },
    [params, filters, setParams]
  );
  const setTripMode = (on) => {
    const p = new URLSearchParams(params);
    if (on) p.set("trip", "1"); else p.delete("trip");
    setParams(p, { replace: true });
  };
  /** Verktøylinje-knappen: desktop = byt fane, mobil = opne tur-draweren. */
  const onToolbarTrip = () => {
    if (desktop) return setTripMode(!tripMode);
    setTripMode(true);
    setTripOpen(true);
  };

  // ---- data ----
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fitKey, setFitKey] = useState("");
  const q = useDebounce(filters.q, 300);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const p = {
      days: filters.days,
      status: filters.status.join(",") || undefined,
      kind: filters.kind || undefined,
      customer_id: filters.customer || undefined,
      q: q || undefined,
    };
    try {
      const d = await fetchMapData(p);
      setData(d);
      // Tilpass utsnittet berre når filteret (ikkje ei vanleg oppfrisking) har endra seg
      setFitKey(JSON.stringify(p));
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunne ikkje hente kartdata.");
    } finally {
      setLoading(false);
    }
  }, [filters.days, filters.status, filters.kind, filters.customer, q]);
  useEffect(() => {
    load();
  }, [load]);

  const machines = useMemo(() => data?.machines || [], [data]);
  const machinesById = useMemo(() => new Map(machines.map((m) => [m.id, m])), [machines]);

  // ---- brukarposisjon ----
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const locate = async () => {
    setLocating(true);
    const pos = await getCurrentPosition();
    setLocating(false);
    if (!pos) return toast.error("Fekk ikkje posisjon. Gi nettlesaren tilgang til posisjon og prøv igjen.");
    setUserPos(pos);
    mapRef.current?.flyTo([pos.lat, pos.lng], Math.max(mapRef.current.getZoom(), 11), { duration: 0.8 });
    toast.success("Posisjonen din er markert. Lista er sortert etter avstand.");
  };

  // ---- detaljar ----
  const [active, setActive] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cluster, setCluster] = useState(null);
  const [clusterOpen, setClusterOpen] = useState(false);
  const [planEvent, setPlanEvent] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [tripOpen, setTripOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const openMachine = (m, { fly = true } = {}) => {
    setActive(m);
    setClusterOpen(false);
    setListOpen(false);
    if (fly && m?.position && mapRef.current) {
      mapRef.current.flyTo([m.position.lat, m.position.lng], Math.max(mapRef.current.getZoom(), 14), { duration: 0.6 });
    }
    setTimeout(() => setSheetOpen(true), fly ? 120 : 0);
  };
  const openCluster = (c) => {
    setCluster(c);
    setClusterOpen(true);
  };

  // ---- turplanlegging ----
  const [selection, setSelection] = useState(() => new Map()); // id -> {kind, order}
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);

  const renumber = (map) => {
    let i = 1;
    const next = new Map();
    for (const [id, s] of map) next.set(id, { ...s, order: i++ });
    return next;
  };
  const toggleSelect = (m) => {
    setSelection((prev) => {
      const next = new Map(prev);
      if (next.has(m.id)) next.delete(m.id);
      else next.set(m.id, { kind: m.next?.kind || m.all_deadlines?.[0]?.kind || "service", order: next.size + 1 });
      return renumber(next);
    });
    if (!tripMode) setTripMode(true);
  };
  const selectMany = (items, on) => {
    setSelection((prev) => {
      const next = new Map(prev);
      for (const m of items) {
        if (on && !next.has(m.id)) next.set(m.id, { kind: m.next?.kind || "service", order: next.size + 1 });
        if (!on) next.delete(m.id);
      }
      return renumber(next);
    });
    if (on && !tripMode) setTripMode(true);
  };
  const removeSel = (id) => setSelection((prev) => { const n = new Map(prev); n.delete(id); return renumber(n); });
  const setKind = (id, kind) => setSelection((prev) => { const n = new Map(prev); if (n.has(id)) n.set(id, { ...n.get(id), kind }); return n; });
  const clearSel = () => { setSelection(new Map()); setPlan(null); };

  // Rekn rekkjefølgje når utvalet / posisjon endrar seg
  const selKey = useMemo(() => [...selection.keys()].join(","), [selection]);
  useEffect(() => {
    if (!selKey) { setPlan(null); return; }
    let cancelled = false;
    setPlanBusy(true);
    routePlan(selKey.split(","), userPos)
      .then((r) => {
        if (cancelled) return;
        setPlan(r);
        // Rekkjefølgja i turen følgjer ruta
        setSelection((prev) => {
          const order = new Map();
          let i = 1;
          for (const s of r.stops) for (const m of s.machines) order.set(m.id, i++);
          const next = new Map();
          for (const [id, s] of prev) next.set(id, { ...s, order: order.get(id) || s.order });
          return next;
        });
      })
      .catch(() => !cancelled && toast.error("Kunne ikkje rekne rekkjefølgje."))
      .finally(() => !cancelled && setPlanBusy(false));
    return () => { cancelled = true; };
  }, [selKey, userPos]);

  const route = useMemo(() => {
    if (!plan?.stops?.length) return null;
    const pts = plan.stops.map((s) => ({ lat: s.lat, lng: s.lng }));
    return userPos ? [userPos, ...pts] : pts;
  }, [plan, userPos]);

  const onPlanned = () => {
    load();
    if (!desktop) setTripOpen(false);
  };

  const summary = data?.summary;
  const showEmpty = !loading && !error && machines.length === 0;

  return (
    <div className="map-page -mx-4 -mb-[calc(var(--mobile-nav-total)+28px)] -mt-5 flex flex-col overflow-hidden sm:-mx-6 sm:-mb-6 sm:-mt-6 lg:-mx-8 lg:-mb-10" data-testid="kart-page">
      <MapToolbar
        filters={filters}
        onChange={setFilters}
        customers={data?.customers || []}
        summary={summary}
        onLocate={locate}
        locating={locating}
        hasUserPos={!!userPos}
        tripMode={tripMode}
        onToggleTrip={onToolbarTrip}
        tripCount={selection.size}
        filterOpen={filterOpen}
        onFilterOpen={setFilterOpen}
      />

      <div className="relative flex min-h-0 flex-1">
        {/* Kart */}
        <div className="relative min-w-0 flex-1" data-testid="map-area">
          <MachineMap
            mapRef={mapRef}
            machines={machines}
            selection={selection}
            activeId={sheetOpen ? active?.id : null}
            route={route}
            userPos={userPos}
            fitKey={fitKey}
            onPin={(m) => openMachine(m, { fly: false })}
            onCluster={openCluster}
          />

          {/* Tilstandar over kartet */}
          {loading && !data && (
            <div className="pointer-events-none absolute inset-0 z-[5] grid place-items-center bg-background/40 backdrop-blur-[1px]" data-testid="map-loading">
              <div className="flex items-center gap-2 rounded-full border bg-card px-4 py-2 text-sm shadow-lg"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Hentar maskiner …</div>
            </div>
          )}
          {loading && data && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-[5] -translate-x-1/2 rounded-full border bg-card/90 px-3 py-1 text-xs shadow backdrop-blur" data-testid="map-refreshing">Oppdaterer …</div>
          )}
          {error && (
            <div className="absolute inset-x-3 top-3 z-[5] sm:left-1/2 sm:right-auto sm:w-[420px] sm:-translate-x-1/2" data-testid="map-error">
              <div className="flex items-center gap-3 rounded-xl border border-[rgba(217,45,32,0.3)] bg-card p-3 text-sm shadow-lg">
                <AlertTriangle className="h-5 w-5 shrink-0 text-[rgb(var(--status-overdue))]" />
                <span className="flex-1">{error}</span>
                <Button size="sm" variant="outline" onClick={load} data-testid="map-retry"><RefreshCw /> Prøv igjen</Button>
              </div>
            </div>
          )}
          {showEmpty && (
            <div className="pointer-events-none absolute inset-x-3 top-3 z-[5] sm:left-1/2 sm:right-auto sm:w-[440px] sm:-translate-x-1/2" data-testid="map-empty">
              <div className="pointer-events-auto rounded-xl border bg-card/95 p-1 shadow-lg backdrop-blur">
                <EmptyState
                  className="border-0 bg-transparent py-6 dark:bg-transparent"
                  icon={MapPinned}
                  title={summary?.unlocated ? "Ingen maskiner med plassering i utvalet" : "Ingen maskiner i utvalet"}
                  description={summary?.unlocated ? `${summary.unlocated} maskin(er) manglar adresse/koordinatar. Legg inn adresse på kunden, så kjem dei på kartet.` : "Utvid tidsrommet eller endre filter for å sjå fleire fristar."}
                  action={filters.days !== 0 && <Button size="sm" variant="outline" onClick={() => setFilters({ days: 0 })} data-testid="map-empty-show-all">Vis alle maskiner</Button>}
                />
              </div>
            </div>
          )}

          {/* Flytande knappar (mobil/tablet) */}
          <div className="absolute inset-x-3 bottom-3 z-[5] flex items-end justify-between gap-2 lg:hidden">
            <Button variant="secondary" className="h-12 rounded-full border bg-card px-4 shadow-lg" onClick={() => setListOpen(true)} data-testid="map-list-button">
              <List /> Liste{summary ? ` · ${summary.located}` : ""}
            </Button>
            <div className="flex flex-col items-end gap-2">
              <Button size="icon" variant="secondary" className="h-12 w-12 rounded-full border bg-card shadow-lg" onClick={locate} disabled={locating} aria-label="Min posisjon" data-testid="map-locate-fab">
                {locating ? <Loader2 className="animate-spin" /> : <LocateFixed className={userPos ? "text-primary" : undefined} />}
              </Button>
              {tripMode && (
                <Button className="h-12 rounded-full px-4 shadow-[0_10px_30px_rgba(255,78,0,0.35)]" onClick={() => setTripOpen(true)} data-testid="map-trip-fab">
                  <Route /> Tur{selection.size ? ` · ${selection.size}` : ""}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Sidepanel (desktop) */}
        {desktop && (
        <aside className="flex w-[380px] shrink-0 flex-col border-l bg-background" data-testid="map-side-panel">
          <Tabs value={tripMode ? "trip" : "list"} onValueChange={(v) => setTripMode(v === "trip")} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="m-2 grid h-10 grid-cols-2">
              <TabsTrigger value="list" data-testid="map-tab-list">Liste{summary ? ` (${summary.located})` : ""}</TabsTrigger>
              <TabsTrigger value="trip" data-testid="map-tab-trip">Tur{selection.size ? ` (${selection.size})` : ""}</TabsTrigger>
            </TabsList>
            <TabsContent value="list" className="mt-0 min-h-0 flex-1 overflow-y-auto">
              <MapList machines={machines} unlocated={data?.unlocated} loading={loading && !data} userPos={userPos} activeId={sheetOpen ? active?.id : null} selection={selection} onOpen={openMachine} onToggleSelect={toggleSelect} sortBy={userPos ? "distance" : "urgency"} />
            </TabsContent>
            <TabsContent value="trip" className="mt-0 min-h-0 flex-1 overflow-hidden">
              <TripPanel selection={selection} machinesById={machinesById} plan={plan} planBusy={planBusy} userPos={userPos} onLocate={locate} onSetKind={setKind} onRemove={removeSel} onClear={clearSel} onPlanned={onPlanned} onOpenMachine={(m) => m && openMachine(m)} />
            </TabsContent>
          </Tabs>
        </aside>
        )}
      </div>

      {/* Mobil: liste-drawer */}
      <Drawer open={listOpen && !desktop} onOpenChange={setListOpen}>
        <DrawerContent className="max-h-[88dvh]" data-testid="map-list-drawer">
          <DrawerHeader>
            <DrawerTitle>Maskiner i utvalet</DrawerTitle>
            <DrawerDescription>{userPos ? "Sortert etter avstand frå deg." : "Sortert etter hast. Trykk «Nær meg» for avstand."}</DrawerDescription>
          </DrawerHeader>
          <DrawerBody className="px-2 pb-[calc(var(--safe-bottom)+12px)]">
            <MapList machines={machines} unlocated={data?.unlocated} loading={loading && !data} userPos={userPos} selection={selection} onOpen={openMachine} onToggleSelect={toggleSelect} sortBy={userPos ? "distance" : "urgency"} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Mobil: tur-drawer */}
      <Drawer open={tripOpen && !desktop} onOpenChange={setTripOpen}>
        <DrawerContent className="h-[86dvh] max-h-[86dvh]" data-testid="map-trip-drawer">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Planlegg tur</DrawerTitle>
            <DrawerDescription>Vald maskiner, rekkjefølgje og dato.</DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 pb-[var(--safe-bottom)]">
            <TripPanel compact selection={selection} machinesById={machinesById} plan={plan} planBusy={planBusy} userPos={userPos} onLocate={locate} onSetKind={setKind} onRemove={removeSel} onClear={() => { clearSel(); setTripOpen(false); setTripMode(false); }} onPlanned={onPlanned} onOpenMachine={(m) => { setTripOpen(false); m && openMachine(m); }} />
          </div>
        </DrawerContent>
      </Drawer>

      <MachineSheet
        machine={active}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        userPos={userPos}
        tripMode={tripMode}
        selected={!!(active && selection.has(active.id))}
        onToggleSelect={(m) => toggleSelect(m)}
        onPlan={(ev) => { setSheetOpen(false); setTimeout(() => setPlanEvent(ev), 80); }}
      />
      <LocationSheet
        cluster={cluster}
        open={clusterOpen}
        onOpenChange={setClusterOpen}
        selection={selection}
        onOpenMachine={(m) => openMachine(m, { fly: false })}
        onToggleSelect={toggleSelect}
        onSelectAll={selectMany}
      />
      <DeadlineActionDialog
        open={!!planEvent}
        onOpenChange={(o) => !o && setPlanEvent(null)}
        event={planEvent}
        mode="plan"
        today={data?.today}
        onDone={() => { load(); setPlanEvent(null); }}
      />

      {/* Skeleton for første last på små skjermar (kartet dekkjer elles) */}
      {loading && !data && !desktop && <Skeleton className="sr-only" />}
      <span className={cn("sr-only")} data-testid="map-trip-mode">{tripMode ? "trip" : "browse"}</span>
    </div>
  );
}
